import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Mail, Phone, MapPin, ChevronLeft, ChevronRight, Download,
  LayoutGrid, List, Users as UsersIcon, ShieldCheck, UserCheck, User,
  BookUser,
} from 'lucide-react';
import API from '../../lib/api';
import AdminFilterBar from './AdminFilterBar';

const ROLE_META = {
  user:        { label: 'User',        color: 'bg-blue-100 text-blue-700',     icon: User },
  ca:          { label: 'CA',          color: 'bg-amber-100 text-amber-700',   icon: UserCheck },
  admin:       { label: 'Admin',       color: 'bg-purple-100 text-purple-700', icon: ShieldCheck },
  super_admin: { label: 'Super Admin', color: 'bg-[#6C63FF]/15 text-[#6C63FF]', icon: ShieldCheck },
};

const SOURCE_META = {
  user:        { label: 'Customer',    color: 'bg-sky-50 text-sky-700 border-sky-200' },
  admin_panel: { label: 'Staff',       color: 'bg-violet-50 text-violet-700 border-violet-200' },
};

const PAGE_SIZE = 20;

export default function MembersDirectory() {
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    status: '',
    source: '',
    has_phone: '',
  });
  const [view, setView] = useState('list'); // 'list' | 'grid'
  const [members, setMembers] = useState([]);
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const { data: res } = await API.get('/admin/all-members');
      setMembers(res.members || []);
    } catch (err) {
      console.error(err);
      setFetchError(err.response?.data?.detail || err.message || 'Failed to load members');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleFilter = (key, val) => {
    setFilters((f) => ({ ...f, [key]: val }));
    setPage(1);
  };
  const handleReset = () => {
    setFilters({ search: '', role: '', status: '', source: '', has_phone: '' });
    setPage(1);
  };

  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return members.filter((m) => {
      if (q) {
        const hay = `${m.name || ''} ${m.email || ''} ${m.phone || ''} ${m.city || ''} ${m.state || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filters.role   && m.role   !== filters.role)   return false;
      if (filters.source && m.source !== filters.source) return false;
      if (filters.status === 'active'   && !m.is_active) return false;
      if (filters.status === 'inactive' &&  m.is_active) return false;
      if (filters.has_phone === 'yes' && !m.phone) return false;
      if (filters.has_phone === 'no'  &&  m.phone) return false;
      return true;
    });
  }, [members, filters]);

  const counts = useMemo(() => {
    const c = { total: members.length, active: 0, inactive: 0, user: 0, ca: 0, admin: 0, super_admin: 0 };
    members.forEach((m) => {
      if (m.is_active) c.active++; else c.inactive++;
      if (c[m.role] !== undefined) c[m.role]++;
    });
    return c;
  }, [members]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const visible    = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const exportCsv = () => {
    const header = ['ID', 'Source', 'Name', 'Email', 'Phone', 'City', 'State', 'Role', 'Status', 'Joined'];
    const rows = filtered.map((m) => [
      m.id,
      SOURCE_META[m.source]?.label || m.source,
      m.name,
      m.email,
      m.phone || '',
      m.city  || '',
      m.state || '',
      m.role,
      m.is_active ? 'Active' : 'Inactive',
      new Date(m.created_at).toISOString().slice(0, 10),
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = 'members-directory.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0F4C5C] via-[#0E7490] to-[#14B8A6] text-white px-6 py-6 mb-5">
        <div className="absolute -top-20 -right-12 w-60 h-60 bg-[#5EEAD4]/25 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-12 w-60 h-60 bg-[#22D3EE]/25 rounded-full blur-3xl" />
        <div className="relative flex items-end justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur-sm border border-white/15 flex items-center justify-center">
              <BookUser className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-heading font-bold leading-tight">
                Members Directory
              </h1>
              <p className="text-sm text-white/75 font-body mt-0.5">
                Customers + staff in one unified view
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 sm:gap-5">
            <div>
              <p className="text-[10px] font-body uppercase tracking-[0.18em] text-white/60">Total</p>
              <p className="text-xl font-heading font-bold mt-0.5">{counts.total}</p>
            </div>
            <div className="h-9 w-px bg-white/15" />
            <div>
              <p className="text-[10px] font-body uppercase tracking-[0.18em] text-white/60">Active</p>
              <p className="text-xl font-heading font-bold mt-0.5">{counts.active}</p>
            </div>
            <div className="h-9 w-px bg-white/15" />
            <div>
              <p className="text-[10px] font-body uppercase tracking-[0.18em] text-white/60">Customers</p>
              <p className="text-xl font-heading font-bold mt-0.5">{counts.user}</p>
            </div>
            <div className="h-9 w-px bg-white/15" />
            <div>
              <p className="text-[10px] font-body uppercase tracking-[0.18em] text-white/60">Staff</p>
              <p className="text-xl font-heading font-bold mt-0.5">{counts.ca + counts.admin + counts.super_admin}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
        <StatCard label="Total"        value={counts.total}        icon={UsersIcon}  tone="text-[#6C63FF]" />
        <StatCard label="Active"       value={counts.active}       icon={UserCheck}  tone="text-green-600" />
        <StatCard label="Inactive"     value={counts.inactive}     icon={UserCheck}  tone="text-red-500" />
        <StatCard label="Customers"    value={counts.user}         icon={User}       tone="text-sky-600" />
        <StatCard label="CAs"          value={counts.ca}           icon={UserCheck}  tone="text-amber-600" />
        <StatCard label="Admins"       value={counts.admin + counts.super_admin} icon={ShieldCheck} tone="text-purple-600" />
      </div>

      <AdminFilterBar filters={filters} onFilterChange={handleFilter} onReset={handleReset}>
        <select
          value={filters.source}
          onChange={(e) => handleFilter('source', e.target.value)}
          className="text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-[#F5F4FB] text-[#1A1A2E] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
        >
          <option value="">All Sources</option>
          <option value="user">Customers</option>
          <option value="admin_panel">Staff</option>
        </select>

        <select
          value={filters.role}
          onChange={(e) => handleFilter('role', e.target.value)}
          className="text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-[#F5F4FB] text-[#1A1A2E] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
        >
          <option value="">All Roles</option>
          <option value="user">User</option>
          <option value="ca">CA</option>
          <option value="admin">Admin</option>
          <option value="super_admin">Super Admin</option>
        </select>

        <select
          value={filters.status}
          onChange={(e) => handleFilter('status', e.target.value)}
          className="text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-[#F5F4FB] text-[#1A1A2E] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>

        <select
          value={filters.has_phone}
          onChange={(e) => handleFilter('has_phone', e.target.value)}
          className="text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-[#F5F4FB] text-[#1A1A2E] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
        >
          <option value="">Any Phone</option>
          <option value="yes">Has Phone</option>
          <option value="no">No Phone</option>
        </select>

        <div className="inline-flex border border-[#E8E6F8] rounded-lg overflow-hidden">
          <button
            onClick={() => setView('list')}
            className={`flex items-center gap-1 px-3 py-2 text-xs font-body transition-colors ${
              view === 'list' ? 'bg-[#6C63FF] text-white' : 'bg-white text-[#6B7280] hover:bg-[#F5F4FB]'
            }`}
            title="List view"
          >
            <List className="h-3.5 w-3.5" /> List
          </button>
          <button
            onClick={() => setView('grid')}
            className={`flex items-center gap-1 px-3 py-2 text-xs font-body transition-colors ${
              view === 'grid' ? 'bg-[#6C63FF] text-white' : 'bg-white text-[#6B7280] hover:bg-[#F5F4FB]'
            }`}
            title="Grid view"
          >
            <LayoutGrid className="h-3.5 w-3.5" /> Grid
          </button>
        </div>

        <button
          onClick={exportCsv}
          disabled={filtered.length === 0}
          className="flex items-center gap-1.5 text-xs font-body text-[#6C63FF] border border-[#6C63FF]/30 hover:bg-[#6C63FF]/10 px-3 py-2 rounded-lg disabled:opacity-40 transition-colors"
        >
          <Download className="h-3.5 w-3.5" />
          Export CSV
        </button>
      </AdminFilterBar>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-[#6C63FF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : fetchError ? (
        <div className="bg-white rounded-2xl border border-[#E8E6F8] p-6 text-center">
          <p className="text-sm text-red-500 font-body">{fetchError}</p>
          <button onClick={load} className="mt-2 text-xs text-[#6C63FF] underline font-body">Retry</button>
        </div>
      ) : visible.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#E8E6F8] p-12 text-center">
          <p className="text-sm text-[#9CA3AF] font-body">No members match your filters</p>
        </div>
      ) : view === 'list' ? (
        <ListView rows={visible} />
      ) : (
        <GridView rows={visible} />
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-[#6B7280] font-body">
            Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className="flex items-center gap-1 text-xs font-body px-3 py-1.5 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </button>
            <span className="text-xs font-body text-[#6B7280]">Page {safePage} / {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className="flex items-center gap-1 text-xs font-body px-3 py-1.5 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] disabled:opacity-40"
            >
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone }) {
  return (
    <div className="bg-white rounded-xl border border-[#E8E6F8] p-3 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-lg bg-[#F5F4FB] flex items-center justify-center ${tone}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-[10px] font-body uppercase tracking-wide text-[#9CA3AF]">{label}</p>
        <p className="text-lg font-heading font-bold text-[#1A1A2E] leading-tight">{value}</p>
      </div>
    </div>
  );
}

function RoleBadge({ role }) {
  const meta = ROLE_META[role] || { label: role, color: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${meta.color}`}>
      {meta.label}
    </span>
  );
}

function SourceBadge({ source }) {
  const meta = SOURCE_META[source] || { label: source, color: 'bg-gray-50 text-gray-600 border-gray-200' };
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${meta.color}`}>
      {meta.label}
    </span>
  );
}

function StatusBadge({ active }) {
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full ${active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

function ListView({ rows }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm font-body">
          <thead>
            <tr className="bg-[#F5F4FB] border-b border-[#E8E6F8]">
              {['Name', 'Source', 'Email', 'Phone', 'Location', 'Role', 'Status', 'Joined'].map((h) => (
                <th key={h} className="px-4 py-3 text-left text-xs font-heading font-semibold text-[#6B7280] whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E8E6F8]">
            {rows.map((m) => (
              <tr key={`${m.source}-${m.id}`} className="hover:bg-[#F5F4FB] transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Avatar name={m.name} photo={m.profile_image} />
                    <div>
                      <p className="font-medium text-[#1A1A2E] leading-tight">{m.name}</p>
                      <p className="font-mono text-[10px] text-[#9CA3AF]">#{m.id}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3"><SourceBadge source={m.source} /></td>
                <td className="px-4 py-3 text-[#6B7280]">
                  {m.email ? (
                    <a href={`mailto:${m.email}`} className="inline-flex items-center gap-1.5 hover:text-[#6C63FF] hover:underline">
                      <Mail className="h-3 w-3" /> {m.email}
                    </a>
                  ) : '–'}
                </td>
                <td className="px-4 py-3 text-[#6B7280] whitespace-nowrap">
                  {m.phone ? (
                    <a href={`tel:${m.phone}`} className="inline-flex items-center gap-1.5 hover:text-[#6C63FF] hover:underline">
                      <Phone className="h-3 w-3" /> {m.phone}
                    </a>
                  ) : <span className="text-[#9CA3AF]">–</span>}
                </td>
                <td className="px-4 py-3 text-[#6B7280] whitespace-nowrap">
                  {(m.city || m.state) ? (
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="h-3 w-3 text-[#9CA3AF]" />
                      {[m.city, m.state].filter(Boolean).join(', ')}
                    </span>
                  ) : <span className="text-[#9CA3AF]">–</span>}
                </td>
                <td className="px-4 py-3"><RoleBadge role={m.role} /></td>
                <td className="px-4 py-3"><StatusBadge active={m.is_active} /></td>
                <td className="px-4 py-3 text-xs text-[#9CA3AF] whitespace-nowrap">
                  {new Date(m.created_at).toLocaleDateString('en-IN')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GridView({ rows }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {rows.map((m) => (
        <div
          key={`${m.source}-${m.id}`}
          className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm p-4 flex flex-col gap-3 hover:shadow-md transition-shadow"
        >
          <div className="flex items-start gap-3">
            <Avatar name={m.name} photo={m.profile_image} large />
            <div className="min-w-0 flex-1">
              <p className="font-heading font-bold text-[#1A1A2E] truncate">{m.name}</p>
              <p className="font-mono text-[10px] text-[#9CA3AF]">#{m.id}</p>
              <div className="flex flex-wrap gap-1 mt-1.5">
                <RoleBadge role={m.role} />
                <SourceBadge source={m.source} />
                <StatusBadge active={m.is_active} />
              </div>
            </div>
          </div>

          <div className="space-y-1.5 text-xs font-body text-[#6B7280] border-t border-[#F0EEF9] pt-3">
            {m.email && (
              <a href={`mailto:${m.email}`} className="flex items-center gap-1.5 truncate hover:text-[#6C63FF]">
                <Mail className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{m.email}</span>
              </a>
            )}
            {m.phone && (
              <a href={`tel:${m.phone}`} className="flex items-center gap-1.5 hover:text-[#6C63FF]">
                <Phone className="h-3 w-3 flex-shrink-0" />
                <span>{m.phone}</span>
              </a>
            )}
            {(m.city || m.state) && (
              <p className="flex items-center gap-1.5">
                <MapPin className="h-3 w-3 flex-shrink-0" />
                {[m.city, m.state].filter(Boolean).join(', ')}
              </p>
            )}
            <p className="text-[10px] text-[#9CA3AF] pt-1">
              Joined {new Date(m.created_at).toLocaleDateString('en-IN')}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

// Resolve uploaded customer avatars (stored as `profiles/<file>` relative
// paths) against the API host so they render across origins.
const resolveImageUrl = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  if (s.startsWith("/uploads/") || s.startsWith("/")) return `${base}${s}`;
  return `${base}/uploads/${s}`;
};

function Avatar({ name, photo, large = false }) {
  const [failed, setFailed] = useState(false);
  const initial = name?.[0]?.toUpperCase() || '?';
  const sizeClasses = large ? 'w-10 h-10 text-base' : 'w-7 h-7 text-xs';
  const src = resolveImageUrl(photo);
  if (src && !failed) {
    return (
      <img
        src={src}
        alt={name || ''}
        onError={() => setFailed(true)}
        className={`${sizeClasses} rounded-full object-cover flex-shrink-0 border border-[#E8E6F8]`}
      />
    );
  }
  return (
    <div className={`${sizeClasses} rounded-full bg-[#6C63FF] flex items-center justify-center text-white font-bold font-heading flex-shrink-0`}>
      {initial}
    </div>
  );
}
